import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import { Alert, ModalButtonWithForm } from 'components/common';
import { FormGroup, Select } from 'components/common/form';
import { AchievementTemplateImportPreview } from '.';

import type { FormState } from 'informed';

interface Props {
  hasExistingItems?: boolean;
  updateTemplate: () => Promise<void>;
}

const AchievementTemplateImport = ({
  hasExistingItems,
  updateTemplate
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const { data } = useApiData({
    url: `/admin/achievements/${achievement.id}/shareable-templates`
  });

  const templateOptions =
    data?.achievements?.map((achievement) => ({
      label: achievement.title,
      value: achievement.id
    })) || [];

  const handleSubmit = async (values: { importTemplateId: number }) => {
    try {
      await api.post(
        `/admin/achievements/${achievement.id}/shareable-templates/${values.importTemplateId}`
      );
      await updateTemplate();

      dispatch(
        actions.addNotice({
          text: messages.achievementTemplateImportSuccess,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <ModalButtonWithForm
      buttonIcon="file-import"
      buttonSize="header"
      buttonText={messages.achievementTemplateImport}
      contentStyle={{ padding: 0 }}
      modalActionText={messages.commonImport}
      modalTitle={messages.achievementTemplateImport}
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {(formState: FormState<{ importTemplateId: number }>) => (
        <div className="inner">
          <div className="header">
            {hasExistingItems ? (
              <Alert
                text={messages.achievementTemplateImportWarning}
                type="warning-light"
              />
            ) : null}

            <FormGroup
              label={messages.achievementTemplateImportSelect}
              maxWidth="320px"
            >
              <Select
                error={formState.errors.importTemplateId}
                field="importTemplateId"
                id="importTemplateId"
                isSearchable={true}
                options={templateOptions}
                placeholder={messages.achievementTemplateImportSelect}
              />
            </FormGroup>
          </div>

          {formState.values.importTemplateId ? (
            <div className="preview">
              <AchievementTemplateImportPreview
                selectedAchievement={data?.achievements?.find(
                  (achievement) =>
                    achievement.id === formState.values.importTemplateId
                )}
              />
            </div>
          ) : null}

          <style jsx>{`
            .header {
              padding: 15px;
            }
            .preview {
              background-color: ${theme.lightGreyLighter};
              border-top: 1px solid ${theme.lightGreyDark};
              padding: 15px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementTemplateImport;
